import React from 'react';
import { ChevronRight, Info, User } from 'lucide-react';
import { Switch } from '../ui/switch';
import { SettingsRow } from './shared';
import useAppSettings from '../../hooks/useAppSettings';

function ManagePanel() {
  return (
    <div className="mt-4 rounded-xl bg-[#F5F1FE] border border-[#E5DEF9] p-4">
      <p className="text-[12.5px] text-[#4A4463] font-semibold mb-3">Manage</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="rounded-xl bg-white p-3">
          <p className="text-[12px] text-[#4A4463] inline-flex items-center gap-1">
            Credits used: <Info size={11} className="text-[#A39DB8]" />
          </p>
          <p className="text-[18px] font-bold text-[#1A1533] mt-1">0</p>
        </div>
        <div className="rounded-xl bg-white p-3">
          <p className="text-[12px] text-[#4A4463]">Total time saved from 24 Jan 2026</p>
          <p className="text-[18px] font-bold text-[#1A1533] mt-1">0 hours</p>
        </div>
      </div>
    </div>
  );
}

function ZaraAvatarLabel() {
  return (
    <span className="inline-flex items-center gap-2">
      <span className="w-5 h-5 rounded-full bg-[#EAE4FB] flex items-center justify-center">
        <User size={10} className="text-[#5B4FE8]" />
      </span>
      Turn on Zara avatar joining the interview
    </span>
  );
}

function TalentPoolLabel() {
  return (
    <span className="inline-flex items-center gap-2">
      Define your talent pool's certification criteria
      <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-[#E9E3FB] text-[#5B4FE8]">New</span>
    </span>
  );
}

function TalentPoolDescription() {
  return (
    <>
      Once enabled, you can set your talent pool's certification criteria for applicants. Candidates who meet it will automatically appear under your own Talent tab. You can then share these profiles with other stakeholders as you see fit.{' '}
      <button className="text-[#5B4FE8] font-semibold hover:underline">Book a demo</button>
    </>
  );
}

function InterviewInvitationLink() {
  return (
    <div className="flex items-center justify-between py-3.5 hover:bg-[#FAF8FF] rounded-lg px-2 -mx-2 cursor-pointer">
      <span className="text-[14px] font-semibold text-[#1A1533]">Interview invitation email</span>
      <ChevronRight size={16} className="text-[#8A83A5]" />
    </div>
  );
}

function ValidityDaysInput({ value, onChange }) {
  // Empty / 0 -> null (never expires). Clamp to 1..365 to match the backend.
  const handle = (e) => {
    const raw = e.target.value.trim();
    if (raw === '') return onChange(null);
    const n = Math.min(365, Math.max(1, parseInt(raw, 10) || 0));
    onChange(n || null);
  };
  return (
    <input
      type="number"
      min={1}
      max={365}
      value={value ?? ''}
      onChange={handle}
      placeholder="Never"
      className="w-24 text-[14px] text-[#1A1533] rounded-lg border border-[#E5DEF9] bg-white px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#5B4FE8]/30"
    />
  );
}

export default function AIInterviewerTab() {
  const { state, update } = useAppSettings();
  const toggle = (key) => (v) => update({ [key]: v });

  return (
    <div>
      <h2 className="text-[18px] font-bold text-[#1A1533]">AI interviewer</h2>
      <ManagePanel />
      <div className="divide-y divide-[#F1ECFE] mt-2">
        <SettingsRow title={<ZaraAvatarLabel />}>
          <Switch checked={state.zara_avatar} onCheckedChange={toggle('zara_avatar')} />
        </SettingsRow>
        <SettingsRow title="Send AI interview completion email to candidate">
          <Switch checked={state.send_completion_email} onCheckedChange={toggle('send_completion_email')} />
        </SettingsRow>
        <SettingsRow title="Allow mobile AI interview">
          <Switch checked={state.allow_mobile} onCheckedChange={toggle('allow_mobile')} />
        </SettingsRow>
        <SettingsRow
          title="Interview invite validity (days)"
          description="How long an interview invite stays open after it's created. Leave blank for no expiry. Once past, the candidate can no longer start or continue the interview."
        >
          <ValidityDaysInput
            value={state.interview_validity_days}
            onChange={(v) => update({ interview_validity_days: v })}
          />
        </SettingsRow>
        <InterviewInvitationLink />
        <SettingsRow
          title="Enforce proctoring"
          description="If proctoring is on for an interview, enforce it. This means candidate will not be able to take the interview without sharing their screen and having their camera on. Please note this is not fully enforceable on Safari or Firefox due to browser limitations."
        >
          <Switch checked={state.enforce_proctoring} onCheckedChange={toggle('enforce_proctoring')} />
        </SettingsRow>
        <SettingsRow title={<TalentPoolLabel />} description={<TalentPoolDescription />}>
          <Switch checked={state.talent_pool_cert} onCheckedChange={toggle('talent_pool_cert')} />
        </SettingsRow>
      </div>
    </div>
  );
}
